import { saveAs } from "file-saver";
import { getFarmSpreadsheetColor } from "./FarmSpreadsheetColor.js";
import { getShopperPrice } from "./FoodGroupingUtils.js";

const columns = [
  { header: "Quantity", width: 12 },
  { header: "Farmer", width: 28 },
  { header: "Product", width: 48 },
  { header: "Unit", width: 28 },
  { header: "Price", width: 14 },
];
const spreadsheetMimeType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function solidFill(argb) {
  return { type: "pattern", pattern: "solid", fgColor: { argb } };
}

function getSpreadsheetFontColor(backgroundColor) {
  // Compare black and white using WCAG relative luminance and contrast ratios.
  // https://www.w3.org/WAI/WCAG22/Techniques/general/G18
  const [red, green, blue] = backgroundColor
    .slice(2)
    .match(/../g)
    .map((hex) => {
      const channel = parseInt(hex, 16) / 255;
      return channel <= 0.04045
        ? channel / 12.92
        : ((channel + 0.055) / 1.055) ** 2.4;
    });
  const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
  const blackContrast = (luminance + 0.05) / 0.05;
  const whiteContrast = 1.05 / (luminance + 0.05);
  return blackContrast >= whiteContrast ? "FF000000" : "FFFFFFFF";
}

function getProductRowHeight(product) {
  const lineCount = Math.max(
    ...product.map((value, index) =>
      String(value)
        .split("\n")
        .reduce(
          (count, line) =>
            count +
            Math.max(
              1,
              Math.ceil(line.length / (columns[index + 1].width - 4)),
            ),
          0,
        ),
    ),
  );
  return Math.max(30, lineCount * 16 + 10);
}

function formatProductRow(row, product, farmColor) {
  const backgroundColor = getFarmSpreadsheetColor(product[0], farmColor);
  const fill = solidFill(backgroundColor);
  const fontColor = getSpreadsheetFontColor(backgroundColor);
  row.height = getProductRowHeight(product);
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.font = { name: "Calibri", size: 11, color: { argb: fontColor } };
    cell.fill = fill;
    cell.alignment = { ...cell.alignment, vertical: "middle", wrapText: true };
    cell.border = { bottom: { style: "hair", color: { argb: "FFFFFFFF" } } };
  });
}

export function getMarketplaceProductRows(
  foodItems,
  selectedFarms,
  userInfo,
  pickupLocationDetails,
) {
  const farms = new Set(selectedFarms);
  const rows = foodItems
    .filter((food) => farms.has(food.farmName))
    .map((food) => {
      const price = getShopperPrice(food, userInfo, pickupLocationDetails);
      if (!Number.isFinite(parseFloat(food.price)) || !Number.isFinite(price)) {
        throw new Error(`The price for ${food.item} is unavailable.`);
      }
      const unit =
        userInfo.userType === "individual"
          ? food.individualDescription || food.description
          : food.description;
      return [food.farmName, food.item, unit || "", Number(price.toFixed(2))];
    });
  // Pantry/catalogue overlap and identical product records should appear once.
  return [
    ...new Map(rows.map((row) => [JSON.stringify(row), row])).values(),
  ].sort(
    (first, second) =>
      first[0].localeCompare(second[0]) ||
      first[1].localeCompare(second[1]) ||
      first[2].localeCompare(second[2]),
  );
}

// farmColors optionally maps exact farm names to #RRGGBB/RRGGBB colors.
// It can be populated from farm records when database colors are introduced.
export async function createMarketplaceWorkbook(productRows, farmColors = {}) {
  if (productRows.length === 0) {
    throw new Error("Select at least one farm with products to download.");
  }

  // Load the styled workbook writer only when a download is requested.
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Marketplace Products", {
    views: [{ state: "frozen", ySplit: 1, showGridLines: false }],
  });
  worksheet.columns = columns;
  worksheet.autoFilter = `A1:E${productRows.length + 1}`;
  worksheet.getColumn(1).alignment = {
    horizontal: "center",
    vertical: "middle",
  };
  worksheet.getColumn(5).numFmt = '"$"#,##0.00';

  const header = worksheet.getRow(1);
  header.height = 30;
  header.eachCell((cell) => {
    cell.font = {
      name: "Calibri",
      size: 11,
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    cell.fill = solidFill("FF285943");
    cell.alignment = { vertical: "middle" };
  });

  productRows.forEach((product) => {
    const row = worksheet.addRow([null, ...product]);
    formatProductRow(row, product, farmColors[product[0]]);
  });
  return workbook;
}

export async function downloadMarketplaceSpreadsheet(productRows, farmColors) {
  const workbook = await createMarketplaceWorkbook(productRows, farmColors);
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: spreadsheetMimeType }),
    "Farm-to-Plate-Marketplace-Products.xlsx",
  );
}
